import React, { useContext, useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Flag, PlusCircle } from 'react-bootstrap-icons'
import { useNavigate, useParams } from 'react-router-dom'
import Authentication from '../../../../libs/api/services/Authentication'
import Patient from '../../../../libs/api/services/Patient'
import QuestionnaireAssignment from '../../../../libs/api/services/QuestionnaireAssignment'
import { ClaimsFilter } from '../../../../libs/authentication/ClaimsFilter'
import CommonFunctionsContext from '../../../common/CommonFunctionsProvider'
import PatientContext from '../../patient/management/PatientProvider'
import FormAssignmentAdmin from '../assignment/FormAssignmentAdmin'
export default function PatientForms() {
    const { setSectionInfo } = useContext(CommonFunctionsContext)
    const { activePatient } = useContext(PatientContext)
    const navigate = useNavigate()
    const { id } = useParams()
    const [mode, setMode] = useState('')
    const [patient, setPatient] = useState()
    const [questionnaires, setQestionnaires] = useState([])
    const [filters, setFilters] = useState({ name: undefined, assigned: undefined, complete: undefined })
    const [sort, setSort] = useState({ field: 'assigned', dir: 'desc' })

    const _handleOnAdd = (v) => {
        setMode('')
        load()
    }

    const _setupAdd = () => {
        Patient.GetById(id)
            .then((r) => {
                setPatient(r)
                setMode('Add')
            })

    }
    useEffect(() => {
        if (activePatient)
            setSectionInfo(`Forms for ${activePatient.userProfile.firstName} ${activePatient.userProfile.lastName} at ${Authentication.getFacilityName()}`);
        else
            setSectionInfo(`Forms`);
    }, [activePatient, setSectionInfo])

    const load = React.useCallback(() => {
        // When doing client-side fuzzy name searching we should not send the name
        // filter to the server because the server may not support fuzzy/leet matching
        // and would return no results. Instead, omit `name` from the request so
        // we always fetch the full set and perform filtering locally.
        const requestFilters = { ...filters }
        if (requestFilters && requestFilters.name) requestFilters.name = undefined
        QuestionnaireAssignment
            .GetPatientQuestionnairesByType(id, 'Form', requestFilters)
            .then((r) => {
                setQestionnaires(r)
            })
    }, [id, filters])

    useEffect(() => {
        load()
    }, [load])

    const _handleSort = (field) => {
        if (sort.field === field)
            setSort({ ...sort, dir: sort.dir === 'asc' ? 'desc' : 'asc' })
        else
            setSort({ field: field, dir: 'asc' })
    }

    const listRefs = useRef([])
    const nameSearchRef = useRef(null)

    const sortedQuestionnaires = React.useMemo(() => {
        if (!questionnaires || questionnaires.length === 0) return []
        const items = [...questionnaires]
        const dir = sort.dir === 'asc' ? 1 : -1
        items.sort((a, b) => {
            if (!a.resource && !b.resource) return 0
            if (!a.resource) return 1
            if (!b.resource) return -1
            switch (sort.field) {
                case 'name':
                    return dir * ((a.resource.questionnaire.name || '').localeCompare(b.resource.questionnaire.name || ''))
                case 'assigned':
                    if (!a.resource.assignedDate && !b.resource.assignedDate) return 0
                    if (!a.resource.assignedDate) return 1
                    if (!b.resource.assignedDate) return -1
                    return dir * (new Date(a.resource.assignedDate).getTime() - new Date(b.resource.assignedDate).getTime())
                default:
                    return 0
            }
        })

        // If a name filter exists, perform client-side fuzzy matching and scoring
        const normalize = (v) => {
            if (!v) return ''
            const map = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '2': 'z', '8': 'b', '6': 'g', '9': 'g' }
            return v.toString().toLowerCase()
                .split('')
                .map(c => map[c] || c)
                .join('')
                .replace(/[^a-z0-9]/g, '')
        }

        const scoreMatch = (name, term) => {
            if (!term || term.length === 0) return 0
            const s = normalize(name)
            const t = normalize(term)
            if (!s || !t) return 0
            if (s === t) return 1000
            if (s.includes(t)) return 800 + (s.indexOf(t) === 0 ? 100 : 0) - t.length
            // subsequence match
            let si = 0, ti = 0
            while (si < s.length && ti < t.length) {
                if (s[si] === t[ti]) ti++
                si++
            }
            if (ti === t.length) return 500 + t.length
            // fallback: simple character overlap
            const common = t.split('').filter((c, i) => s.indexOf(c) !== -1).length
            return common > 0 ? 100 + common : 0
        }

        if (filters && filters.name) {
            const term = filters.name
            const scoredRaw = items.map(i => ({
                item: i,
                score: i.resource && i.resource.questionnaire ? scoreMatch(i.resource.questionnaire.name || '', term) : 0
            }))
            const scored = scoredRaw.filter(x => x.score > 0)
                .sort((a, b) => b.score - a.score)
                .map(x => x.item)
            return scored
        }

        return items
    }, [questionnaires, sort, filters])

    useEffect(() => {
        // trim refs array to match items
        listRefs.current = listRefs.current.slice(0, sortedQuestionnaires.length)
    }, [sortedQuestionnaires])

    const handleListKeyDown = (e) => {
        if (e.key === 'Escape') {
            if (nameSearchRef.current && typeof nameSearchRef.current.focus === 'function') {
                nameSearchRef.current.focus()
                e.preventDefault()
            }
            return
        }
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
        const items = listRefs.current
        if (!items || items.length === 0) return
        const active = document.activeElement
        const idx = items.findIndex(x => x === active)
        let nextIdx = -1
        if (idx === -1) {
            // focus first (down) or last (up)
            nextIdx = e.key === 'ArrowDown' ? 0 : items.length - 1
        } else {
            nextIdx = e.key === 'ArrowDown' ? Math.min(items.length - 1, idx + 1) : Math.max(0, idx - 1)
        }
        const next = items[nextIdx]
        if (next && typeof next.focus === 'function') {
            next.focus()
            e.preventDefault()
        }
    }

    if (mode === "Add" && patient)
        return (<FormAssignmentAdmin
            onDone={(v) => _handleOnAdd(v)}
            onCancel={(e) => setMode('')}
            skip={[2]}
            startAt={1}
            defaultValues={{ user: patient }}
        />)

    return (
        <div onKeyDown={handleListKeyDown} tabIndex={0}>
            <ClaimsFilter resources={[{ resource: "Forms", type: 'Edit' }, { resource: "Forms", type: 'Inherit' }]} claim={[]}>
                <div className='d-flex flex-row text-center p-3 bg-secondary-container'>

                    <div
                        onClick={(e) => _setupAdd()}
                        style={{ cursor: 'pointer' }} className=' bg-primary-container m-2 p-2 rounded '>
                        <PlusCircle
                        /> New Form
                    </div>

                    {/* <div style={{ cursor: 'pointer' }} className=' bg-primary-container m-2 p-2 shadow '>
                        Assigned (100)
                    </div>
                    <div style={{ cursor: 'pointer' }} className=' bg-secondary-container m-2 p-2'>
                        Complete (99)
                    </div>
                    <div style={{ cursor: 'pointer' }} className=' bg-secondary-container m-2 p-2'>
                        Incomplete (1)
                    </div> */}
                </div>
            </ClaimsFilter>
            <div className='row text-center my-2'>
                <div className='col-5 d-flex flex-column' style={{ cursor: 'pointer' }} onClick={() => _handleSort('name')}>
                    <div><b>Name</b> {sort.field === 'name' ? (sort.dir === 'asc' ? <ChevronUp /> : <ChevronDown />) : ''}</div>
                    <div className='m-2'>
                        <input ref={nameSearchRef} autoFocus onChange={(e) => setFilters({ ...filters, name: e.target.value === '' ? undefined : e.target.value })} className='form-control form-control-sm' placeholder='Search' style={{ flex: 1 }} />
                    </div>
                </div>
                <div className='col-3 d-flex flex-column' style={{ cursor: 'pointer' }} onClick={() => _handleSort('assigned')}>
                    <div><b>Requested On</b> {sort.field === 'assigned' ? (sort.dir === 'asc' ? <ChevronUp /> : <ChevronDown />) : ''}</div>
                    <input onChange={(e) => setFilters({ ...filters, assigned: !e.target.value ? undefined : e.target.value.toString() })} type='date' className='form-control form-control-sm m-2' placeholder='Search' />
                </div>
                <div className='col-2'>
                    <b>Completed On</b>
                </div>
                <div className='col-2'>
                    <b>Options</b>
                </div>
            </div>

            {
                sortedQuestionnaires.map((q, idx) => q.resource &&
                    <div key={q.id} className='row'>
                        <div className='col-5'>
                            <Flag className='text-danger me-2' />
                            {q.resource.questionnaire.name}
                        </div>
                        <div className='col-3'>
                            {q.resource.assignedDate ? q.resource.assignedDate.substring(0, 19) : ''}
                        </div>
                        <div className='col-2'>
                            {q.resource.completedDate ? q.resource.completedDate.substring(0, 19) : ''}
                        </div>
                        <div className='col-2 text-center'>
                            <button
                                ref={(el) => listRefs.current[idx] = el}
                                aria-label={`View ${q.resource.questionnaire.name}`}
                                onClick={(e) => navigate('/Dashboard/Patient/' + id + '/Form/' + q.resource.questionnaire.id + "/" + q.resource.id)}
                                className='btn btn-sm'>View</button>
                        </div>
                    </div>
                )
            }
        </div>
    )
}